import { renderDeathEventType } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DeathState, formatDeathState } from '@/domain/deathData/deathData'
import { ParcelContractEvent } from '@/domain/parcels/parcel'
import { useUser } from '@/hooks/api/useUsers'
import { format, isValid } from 'date-fns'

const formatValue = (value: any, field?: string) => {
  if (value === null || value === undefined || value === '') return ''

  if (field === 'state') return formatDeathState(value as DeathState)

  if (typeof value === 'boolean') return value ? 'Sí' : 'No'

  if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
    const date = new Date(value)
    if (isValid(date) && !isNaN(date.getTime())) {
      return format(date, 'dd/MM/yyyy')
    }
  }

  return String(value)
}

const fieldTranslations: Record<string, string> = {
  name: 'Nombre',
  docType: 'Tipo de Doc.',
  docNumber: 'Nº Documento',
  birthDate: 'Fecha de Nacimiento',
  deathDate: 'Fecha de Fallecimiento',
  state: 'Estado',
  notes: 'Notas',
  entryNumber: 'Nº de Acta',
  year: 'Año',
  volume: 'Tomo',
  leaf: 'Folio',
  office: 'Oficina/Registro',
  mortuary: 'Funeraria',
  medicOfDeceased: 'Médico',
  deathCertificateAuthorization: 'Autorización de Sepultura',
  relationshipWithDeceased: 'Relación del fallecido con el titular',
  thumbLevel: 'Nivel en la Parcela',
  cremationDate: 'Fecha de Cremación',
  intermentDate: 'Fecha de Inhumación',
  disintermentDate: 'Fecha de Exhumación',
  reductionDate: 'Fecha de Reducción',
  transferredTo: 'Trasladado a',
}

const translateField = (field: string) => fieldTranslations[field] || field

interface EventPayloadModalProps {
  isOpen: boolean
  onClose: () => void
  event: ParcelContractEvent
}

export const EventPayloadModal = ({ isOpen, onClose, event }: EventPayloadModalProps) => {
  if (!event || !event.payload) return null
  const { data: user } = useUser(event.createdBy!)
  const { added = [], updated = [], removed = [] } = event.payload
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalles del Evento Manual</DialogTitle>
          <DialogDescription>
            {renderDeathEventType(event.eventType)} - {event.eventDate && format(new Date(event.eventDate), "dd/MM/yyyy HH:mm")} - Usuario: {user?.fullName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {added.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-green-700 flex items-center gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Agregados</Badge>
              </h3>
              <div className="space-y-4 pl-4">
                {added.map((a: any, i: number) => (
                  <div key={i} className="border-l-2 border-green-200 pl-3">
                    <p className="font-medium text-sm mb-2">{a.name || 'Sin nombre'}</p>
                    <div className="space-y-1">
                      {Object.entries(a).map(([key, val], j) => {
                        if (key === 'id' || key === 'name' || key === 'contractId' || key === 'createdBy' || key === 'updatedBy') return null;
                        return (
                          <div key={j} className="text-xs grid grid-cols-[2fr_3fr] gap-2 items-center bg-slate-50 p-1 rounded">
                            <span className="font-semibold text-slate-600">{translateField(key)}:</span>
                            <span className="text-green-600 font-medium truncate" title={formatValue(val, key)}>
                              {formatValue(val, key)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {updated.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-blue-700 flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Modificados</Badge>
              </h3>
              <div className="space-y-4 pl-4">
                {updated.map((u: any, i: number) => (
                  <div key={i} className="border-l-2 border-blue-200 pl-3">
                    <p className="font-medium text-sm mb-2">{u.name || 'Sin nombre'}</p>
                    <div className="space-y-1">
                      {u.changes?.map((change: any, j: number) => (
                        <div key={j} className="text-xs grid grid-cols-[2fr_2fr_1fr_2fr] gap-2 items-center bg-slate-50 p-1 rounded">
                          <span className="font-semibold text-slate-600">{translateField(change.field)}:</span>
                          <span className="text-red-500 line-through truncate" title={formatValue(change.previousValue, change.field)}>
                            {formatValue(change.previousValue, change.field)}
                          </span>
                          <span className="text-slate-400">{'->'}</span>
                          <span className="text-green-600 font-medium truncate" title={formatValue(change.newValue, change.field)}>
                            {formatValue(change.newValue, change.field)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {removed.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-red-700 flex items-center gap-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Eliminados</Badge>
              </h3>
              <div className="space-y-4 pl-4">
                {removed.map((r: any, i: number) => (
                  <div key={i} className="border-l-2 border-red-200 pl-3">
                    <p className="font-medium text-sm mb-2">{r.name || 'Sin nombre'}</p>
                    <div className="space-y-1">
                      {Object.entries(r).map(([key, val], j) => {
                        if (key === 'id' || key === 'name' || key === 'contractId' || key === 'createdBy' || key === 'updatedBy' || key === 'createdAt' || key === 'updatedAt') return null;
                        if (val === null || val === undefined || val === '') return null;
                        return (
                          <div key={j} className="text-xs grid grid-cols-[2fr_3fr] gap-2 items-center bg-slate-50 p-1 rounded">
                            <span className="font-semibold text-slate-600">{translateField(key)}:</span>
                            <span className="text-red-600 font-medium truncate" title={formatValue(val, key)}>
                              {formatValue(val, key)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
